import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Drawer, IconButton, Stack, Typography, Button, Alert, TextField, InputAdornment,
} from '@mui/material';
import { Add, Close, Search } from '@mui/icons-material';
import MasterRecordTable, { type MasterRecordRow } from './MasterRecordTable';
import MasterFormDrawer, { type MasterFormState } from './MasterFormDrawer';
import ConfirmDialog from '../ui/ConfirmDialog';
import {
  createMaster, deleteMaster, getMasters, getMasterSchema, updateMaster,
} from '../../services/platform/masterService';
import { enrichMetadataFields } from '../../constants/masterRefFields';
import { slugifyMasterCode } from '../../constants/onDemandMasters';

export interface CategoryParentRow {
  _id: string;
  code: string;
  name: string;
}

interface Props {
  open: boolean;
  parent: CategoryParentRow | null;
  onClose: () => void;
}

const emptyForm = (parentId = ''): MasterFormState => ({
  code: '',
  name: '',
  description: '',
  country: '',
  state: '',
  city: '',
  district: '',
  parentId,
  sortOrder: 0,
  isActive: true,
  metadata: { status: 'active' },
});

export default function CategoryChildrenDrawer({ open, parent, onClose }: Props) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MasterRecordRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MasterRecordRow | null>(null);
  const [form, setForm] = useState<MasterFormState>(emptyForm());
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDebouncedSearch('');
      setPaginationModel({ page: 0, pageSize: 25 });
      setDialogOpen(false);
      setEditing(null);
      setDeleteTarget(null);
      setError('');
    }
  }, [open]);

  const parentId = parent?._id || '';

  const { data: schemaRaw } = useQuery({
    queryKey: ['master-schema', 'service_categories'],
    queryFn: () => getMasterSchema('service_categories'),
    enabled: open,
  });
  const schema = useMemo(
    () => (schemaRaw
      ? { ...schemaRaw, metadataFields: enrichMetadataFields(schemaRaw.metadataFields, 'service_categories') }
      : schemaRaw),
    [schemaRaw],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['masters', 'service_categories', 'children', parentId, debouncedSearch, paginationModel.page, paginationModel.pageSize],
    queryFn: () => getMasters('service_categories', {
      parentId,
      search: debouncedSearch || undefined,
      limit: paginationModel.pageSize,
      page: paginationModel.page + 1,
    }),
    enabled: open && !!parentId,
  });

  const rows = (data?.data || []) as MasterRecordRow[];
  const totalRows = data?.meta?.total ?? 0;

  const parentOptions = useMemo(
    () => (parent ? [{ _id: parent._id, code: parent.code, name: parent.name }] : []),
    [parent],
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm(parentId));
    setDialogOpen(true);
    setError('');
  };

  const openEdit = (row: MasterRecordRow) => {
    const meta: Record<string, string | number | boolean> = {};
    Object.entries(row.metadata || {}).forEach(([k, v]) => {
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') meta[k] = v;
    });
    const isActive = row.isActive !== false;
    if (meta.status === undefined) meta.status = isActive ? 'active' : 'inactive';
    setEditing(row);
    setForm({
      code: row.code,
      name: row.name,
      description: row.description || '',
      country: '',
      state: '',
      city: '',
      district: '',
      parentId,
      sortOrder: row.sortOrder || Number(meta.sortOrder) || 0,
      isActive,
      metadata: meta,
    });
    setDialogOpen(true);
    setError('');
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const metadata: Record<string, unknown> = {
        ...form.metadata,
        status: form.isActive === false ? 'inactive' : 'active',
        module: 'on_demand',
        parentCode: parent?.code || null,
      };
      if (schema?.metadataFields?.some((f) => f.key === 'sortOrder')) {
        metadata.sortOrder = form.sortOrder;
      }
      const code = form.code || slugifyMasterCode(form.name);
      const payload: Record<string, unknown> = {
        code,
        name: form.name,
        description: form.description || undefined,
        sortOrder: form.sortOrder,
        isActive: form.isActive !== false,
        parentId,
        metadata,
      };
      if (editing) return updateMaster(editing._id, payload);
      return createMaster('service_categories', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masters', 'service_categories'] });
      queryClient.invalidateQueries({ queryKey: ['masters-count', 'service_categories'] });
      queryClient.invalidateQueries({ queryKey: ['master-options'] });
      queryClient.invalidateQueries({ queryKey: ['on-demand-categories'] });
      setDialogOpen(false);
      setEditing(null);
      setError('');
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      setError(err.response?.data?.message || 'Save failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteMaster(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masters', 'service_categories'] });
      queryClient.invalidateQueries({ queryKey: ['masters-count', 'service_categories'] });
      queryClient.invalidateQueries({ queryKey: ['master-options'] });
      queryClient.invalidateQueries({ queryKey: ['on-demand-categories'] });
      setDeleteTarget(null);
    },
  });

  const handleToggleActive = useCallback((row: MasterRecordRow, active: boolean) => {
    updateMaster(row._id, {
      code: row.code,
      name: row.name,
      description: row.description,
      isActive: active,
      parentId,
      metadata: { ...(row.metadata || {}), status: active ? 'active' : 'inactive' },
    }).then(() => {
      queryClient.invalidateQueries({ queryKey: ['masters', 'service_categories'] });
    });
  }, [parentId, queryClient]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open && !!parent}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 640 } } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={800} noWrap>Sub-categories</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              Under {parent?.name} ({parent?.code})
            </Typography>
          </Box>
          <Button
            size="small"
            variant="contained"
            startIcon={<Add />}
            onClick={openCreate}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Add sub-category
          </Button>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Add, edit, or delete sub-categories for this primary category. Parent create/edit stays on the main list.
            </Typography>
            <TextField
              size="small"
              placeholder="Search sub-categories…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18 }} color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 280 }}
            />
            {error && (
              <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
            )}
            <MasterRecordTable
              rows={rows}
              schema={schema}
              masterTypeKey="service_categories"
              loading={isLoading}
              totalCount={totalRows}
              page={paginationModel.page}
              pageSize={paginationModel.pageSize}
              onPageChange={(page) => setPaginationModel((p) => ({ ...p, page }))}
              onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
              onToggleActive={handleToggleActive}
            />
          </Stack>
        </Box>
      </Drawer>

      <MasterFormDrawer
        open={dialogOpen}
        editing={!!editing}
        schema={schema}
        currentType="service_categories"
        form={form}
        onChange={setForm}
        onClose={() => setDialogOpen(false)}
        onSubmit={() => saveMutation.mutate()}
        loading={saveMutation.isPending}
        parentMasterType="service_categories"
        parentOptions={parentOptions}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete sub-category?"
        message={`Remove "${deleteTarget?.name}" (${deleteTarget?.code})? This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
      />
    </>
  );
}
